import styles from "./MobileHeader.module.scss";
import { HeadSearch } from "../enhancedSearch/HeadSearch/HeadSearch";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useScreenSize from "../../hooks/useScreenSize";
import useAppModernisation from "../../hooks/useAppModernisation";
import { useState, useEffect } from "react";
import classNames from "classnames";
import OnlineIcon from "../../assets/icons/AccountWeb.svg?react";
import AnimatedPopup from "../AnimatedComponents/AnimatedPopup";
import AppNavigation from "./AppNavigation";
import { copyAccessKey } from "../../utils/copyAccessKey";
import { apiService } from "../../api/apiAdapter";
import { isSalesforceEnabled } from "../../utils/featureFlagSalesforceUtils";
import AppsMenu from "../AppsMenu/AppsMenu";
import useAuth from "../../hooks/useAuth";
import useNetworkStatus from "../../hooks/useNetworkStatus";
import { getReferrer } from "../../utils/authUtils";

const MobileHeader = () => {
  const push = useNavigate();
  const { isDesktop } = useScreenSize();
  const { isAppMode } = useAppModernisation();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [institutionStatus, setInstitutionStatus] = useState(null);
  const [institution, setInstitution] = useState(null);
  const referral = getReferrer();
  const {
    subscriptionDetails,
    isLoading = false,
    subscriptionError = null,
    subscriptionCheckAttempted = false,
  } = useAuth();
  const { isOnline } = useNetworkStatus();
  const shouldShowApps =
    !isLoading &&
    !subscriptionError &&
    !!subscriptionDetails &&
    subscriptionCheckAttempted &&
    !subscriptionDetails.isMultiUser &&
    !isAppMode &&
    isOnline;

  const expandSideBar = () => setIsExpanded(!isExpanded);
  const handleMobileNavigate = () => setIsExpanded(false);

  const is_mobile =
    window.innerWidth < 768 ||
    (window.innerWidth >= 768 && window.innerWidth <= 1024 && window.innerHeight >= 600) ||
    (window.innerWidth >= 1024 && window.innerWidth <= 1366 && window.innerHeight >= 768);

  useEffect(() => {
    try {
      const tgt = localStorage.getItem("tgt");
      const expiryRaw = localStorage.getItem("tg_expirydate");

      if (tgt && expiryRaw) {
        const decodedInstitution = JSON.parse(atob(tgt));
        const expiryDate = new Date(JSON.parse(atob(expiryRaw)));
        if (decodedInstitution && expiryDate > new Date()) {
          setInstitution(decodedInstitution);
        }
      }
    } catch (error) {
      console.error("Failed to parse institution or referrer:", error);
    }
  }, []);
  const fetchInstitution = async () => {
    if (is_mobile && institution?.id && referral) {
      try {
        const expiryRaw = localStorage.getItem("tg_expirydate");
        const expiryDate = new Date(JSON.parse(atob(expiryRaw)));
        if (!expiryRaw || expiryDate < new Date()) {
          //expired -recheck
          const result = await apiService.getInstitutionApi(institution.id);
          setInstitutionStatus(result);
        } else {
          // console.log("salesforce fetchInstitution - token not expired, skipping API call");
          setInstitutionStatus({ expired: false });
        }
      } catch (error) {
        console.error("Failed to fetch institution:", error);
        setInstitutionStatus({ expired: true });
      }
    }
  };
  const salesforceFetchInstitution = async () => {
    if (is_mobile && institution?.id && referral && institution?.instSubscriptionId) {
      // institution?.id is account id in salesforce
      try {
        const expiryRaw = localStorage.getItem("tg_expirydate");
        const expiryDate = new Date(JSON.parse(atob(expiryRaw)));
        if (!expiryRaw || expiryDate < new Date()) {
          const result = await apiService.getInstitutionApi(institution.instSubscriptionId); // object institutionSubscription Id in saleseforce
          setInstitutionStatus(result);
          // console.log("salesforce setInstitutionStatus expired status:", result);
        } else {
          // console.log("salesforce fetchInstitution - token not expired, skipping API call");
          setInstitutionStatus({ expired: false });
        }
      } catch (error) {
        console.error("Failed to fetch salesforce institution:", error);
        setInstitutionStatus({ expired: true });
      }
    }
  };

  useEffect(() => {
    isSalesforceEnabled() ? salesforceFetchInstitution() : fetchInstitution();
  }, [is_mobile, institution?.instSubscriptionId, referral]);
  // console.log("MobileHeader institution status:", institutionStatus, "referral:", referral, "institution:", institution, "is_mobile:", is_mobile);

  const shouldShowCopyAccessKey = () =>
    is_mobile && institution && referral && institutionStatus && !institutionStatus.expired;

  useEffect(() => {
    const handleClickOutside = (e) => {
      const accountMenus = document.querySelectorAll(`.${styles.accountSection}`);
      const clickedInside = [...accountMenus].some((menu) => menu.contains(e.target));

      if (!clickedInside) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div>
      <div className={classNames(styles.appHeader)}>
        <button
          onClick={expandSideBar}
          className={classNames("size-12", styles.menuButton)}
          data-testid="expand-button"
        >
          <i className="tg-icons-Menu size-14"></i>
        </button>

        <Link key="home-link" to="/">
          <img
            src="/TherapeuticGuidelines_Original_R_Logo.svg"
            alt="Therapeutic Guidelines"
            className={styles.logo}
            data-testid="expanded-logo"
          />
        </Link>

        <div className={styles.accountSection}>
          {shouldShowApps && <AppsMenu iconSize={32} />}

          <button
            id="account-person"
            className={styles.accountButton}
            onClick={() => setOpen(!open)}
            aria-haspopup="true"
            aria-expanded={open}
          >
            <OnlineIcon className={styles.accountIcon} />
          </button>

          {open && (
            <div className={styles.dropdownMenu}>
              <Link to="/app/accountLanding" className={styles.menuItem}>
                Access My Account
              </Link>

              {shouldShowCopyAccessKey() && (
                <button className={styles.copyButton} onClick={copyAccessKey}>
                  Copy Access Key
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <AnimatedPopup show={isExpanded} direction="top">
        <div>
          <div className={classNames(styles.appHeader, styles.expandedHeader)}>
            <button
              onClick={expandSideBar}
              className={classNames("size-12", styles.menuButton)}
              data-testid="close-button"
            >
              <i className="tg-icons-Cross size-20"></i>
            </button>

            <HeadSearch
              classicView={true}
              {...{
                push,
                isDesktop,
                isAppMode,
                searchQuery: location.search,
              }}
            />

            <div className={styles.accountSection}>
              <button
                className={styles.accountButton}
                onClick={() => setOpen(!open)}
                aria-haspopup="true"
                aria-expanded={open}
              >
                <OnlineIcon className={styles.accountIcon} />
              </button>

              {open && (
                <div className={styles.dropdownMenu}>
                  <Link to="/app/accountLanding" className={styles.menuItem}>
                    Access My Account
                  </Link>

                  {shouldShowCopyAccessKey() && (
                    <button className={styles.copyButton} onClick={copyAccessKey}>
                      Copy Access Key
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <AppNavigation
            styles={styles}
            isExpanded={true}
            variant="mobile"
            onNavigate={handleMobileNavigate}
          />
        </div>
      </AnimatedPopup>

      <div onClick={expandSideBar} className={isExpanded ? styles.greyedOut : ""}></div>
    </div>
  );
};

export default MobileHeader;
